import useGetSupplierPurchaseOrders from '@/api/queries/suppliers/useGetSupplierPurchaseOrders';
import { usePaginationState } from '@/ui/pages/projectPage/components/MaterialsPanel/materialsPanelHooks';
import { Button, ManageableItemsList } from '@payaca/ambrosia';

import { useDebounce } from '@payaca/hooks';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { createIsolatedPurchaseOrder } from '@payaca/store/materialsList/materialsListActions';
import { Supplier } from '@payaca/types/supplierTypes';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import supplierKeys from '../../../api/queries/suppliers/keyFactory';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import { PurchaseOrdersManageableItemsListTable } from '../purchaseOrdersManageableItemsListTable/PurchaseOrdersManageableItemsListTable';

type Props = {
  supplierId: Supplier['id'];
};

const SupplierPurchaseOrders: FC<Props> = ({
  supplierId,
}: Props): JSX.Element => {
  const queryClient = useQueryClient();
  const { pagination, setCurrentPage } = usePaginationState(20);
  const [searchTerm, setSearchTerm] = useState<string>();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>();

  const [, cancel] = useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    300,
    [searchTerm]
  );

  const { purchaseOrders } = useGetSupplierPurchaseOrders({
    supplierId,
    pagination,
    input: {
      searchTerm: debouncedSearchTerm,
    },
  });

  const dispatch = useDispatch();
  const history = useHistory();

  const [isCreatingPurchaseOrder, setIsCreatingPurchaseOrder] = useState(false);

  return (
    <ManageableItemsList>
      <ManageableItemsList.HeaderBar
        heading="Purchase Orders"
        buttons={
          <PermissionGuard
            renderIfHasPermissions={[
              MaterialsListPermissions.PERSIST_ISOLATED_PURCHASE_ORDER,
            ]}
          >
            <div className="ml-auto shrink">
              <Button
                isProcessing={isCreatingPurchaseOrder}
                size={'sm'}
                onClick={() => {
                  if (isCreatingPurchaseOrder) return;
                  setIsCreatingPurchaseOrder(true);
                  dispatch(
                    createIsolatedPurchaseOrder.request({
                      payload: { supplierId: supplierId },
                      callback: (purchaseOrder) => {
                        setIsCreatingPurchaseOrder(false);
                        history.push(
                          `/purchaseOrders/${purchaseOrder.id}/edit`
                        );
                      },
                      onErrorCallback: () => {
                        setIsCreatingPurchaseOrder(false);
                      },
                    })
                  );
                }}
              >
                Create Purchase Order
              </Button>
            </div>
          </PermissionGuard>
        }
      />
      <ManageableItemsList.ActionBar>
        <ManageableItemsList.ActionBar.SearchInput onChange={setSearchTerm} />
      </ManageableItemsList.ActionBar>
      <PurchaseOrdersManageableItemsListTable
        purchaseOrders={purchaseOrders?.items || []}
        isLoading={!purchaseOrders}
        includedColumns={[
          'ref',
          'status',
          'project',
          'project-customer',
          'purchase-cost',
          'forecast-cost',
        ]}
        onUpdateSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: supplierKeys.supplier(supplierId.toString()),
          })
        }
      ></PurchaseOrdersManageableItemsListTable>
      {purchaseOrders && (
        <ManageableItemsList.PaginationBar
          type="custom"
          totalItems={purchaseOrders.totalCount}
          pageSize={purchaseOrders.limit}
          currentPage={purchaseOrders.offset / purchaseOrders.limit + 1}
          onPageChange={setCurrentPage}
        />
      )}
    </ManageableItemsList>
  );
};

export default SupplierPurchaseOrders;
